import { useState } from 'react'
import { Info, LogOut, Smartphone } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useDeviceMutations, useMyDevices } from '../api'
import { PLATFORM_LABELS } from '../types'
import type { MobileDeviceView } from '../types'

/**
 * Where this account is signed in.
 *
 * <p>The reason this screen exists is not tidiness. Somebody who loses a phone has no way to
 * end that session otherwise — the alternative is changing the password, which signs out every
 * device including the one they are holding. Signing out one device revokes that device's
 * refresh family and leaves the rest alone.
 */
export function MyDevicesPage() {
  useBreadcrumb()
  const devices = useMyDevices()
  const { revoke } = useDeviceMutations()

  const [toRevoke, setToRevoke] = useState<MobileDeviceView | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onRevoke() {
    if (!toRevoke) return
    setError(null)
    try {
      await revoke.mutateAsync({ id: toRevoke.id })
      setToRevoke(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That device could not be signed out')
    }
  }

  const live = devices.data?.filter((device) => !device.revokedAt) ?? []

  return (
    <div className="space-y-4">
      <PageHeader
        title="My devices"
        subtitle="Every phone and tablet signed in as you."
      />

      {error && (
        <Card role="alert" className="border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </Card>
      )}

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Signing a device out ends its session immediately and leaves every other device signed
        in. A device you do not recognise is worth signing out and then changing your password.
      </p>

      <Card>
        {devices.isPending && <Skeleton className="m-4 h-24" />}
        {devices.isError && (
          <ErrorState error={devices.error} onRetry={() => void devices.refetch()} />
        )}
        {devices.isSuccess && devices.data.length === 0 && (
          <EmptyState
            title="No devices registered"
            description="A device appears here the first time the mobile app signs in on it. A browser does not register as a device."
          />
        )}
        {devices.isSuccess && devices.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">Device</th>
                  <th className="p-3 font-medium">Notifications</th>
                  <th className="p-3 font-medium">Last seen</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {devices.data.map((device) => (
                  <tr key={device.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <span className="flex items-center gap-2">
                        <Smartphone className="size-4 text-slate-500" aria-hidden="true" />
                        {device.model ?? PLATFORM_LABELS[device.platform] ?? device.platform}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {PLATFORM_LABELS[device.platform] ?? device.platform}
                        {device.appVersion ? ` · app ${device.appVersion}` : ''}
                        {device.lastIp ? ` · ${device.lastIp}` : ''}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">
                      {device.pushRegistered
                        ? `On, over ${device.pushTransport ?? 'push'}`
                        : 'Off — this device will not be notified'}
                    </td>
                    <td className="p-3 text-slate-600">
                      {device.lastSeenAt
                        ? <DateValue value={device.lastSeenAt} />
                        : 'Never used since registering'}
                    </td>
                    <td className="p-3">
                      {device.revokedAt
                        ? <StatusBadge status="REVOKED" />
                        : <StatusBadge status={device.current ? 'THIS DEVICE' : 'ACTIVE'} />}
                      {device.revokedReason && (
                        <span className="block text-xs text-slate-500">
                          {device.revokedReason}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      {!device.revokedAt && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setError(null); setToRevoke(device) }}
                        >
                          <LogOut className="size-4" aria-hidden="true" />
                          Sign out
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {live.length > 0 && (
        <p className="text-xs text-slate-500">
          {live.length} device{live.length === 1 ? '' : 's'} currently signed in.
        </p>
      )}

      <ConfirmDialog
        open={toRevoke != null}
        onOpenChange={(open) => { if (!open) setToRevoke(null) }}
        title="Sign this device out?"
        description={
          toRevoke?.current
            ? 'This is the device you are using. Signing it out will end this session.'
            : `${toRevoke?.model ?? 'That device'} will be signed out immediately and will have to sign in again.`
        }
        confirmLabel="Sign out"
        onConfirm={() => void onRevoke()}
      />
    </div>
  )
}
